import {
  buildWorkoutOverviewStatusSourceFromPlanWorkout,
  formatWorkoutScheduleParts,
  groupWorkoutsByPlanDate,
} from '~/utils/clientTrainingPlanMainView';
import {
  TRAINING_DELIVERY_FORMAT_FILTER_OPTIONS,
  TRAINING_DELIVERY_FORMAT_UNSET,
  resolveWorkoutDeliveryFormatKey,
} from '~/utils/trainingDeliveryFormat';
import {
  resolveWorkoutOverviewStatusFilterVariant,
  WORKOUT_OVERVIEW_STATUS_FILTERS,
} from '~/utils/workoutOverviewStatus';
import {
  ALL_WORKOUT_STATUS_FILTER_VALUES,
  normalizeWorkoutOverviewStatusFilterValue,
  resolveWorkoutStatusFilterKeys,
} from '~/utils/workoutCompletedSubstatusFilters';
import {
  formatCompactWeekDateRangeLabel,
} from '~/utils/weekDateRangeLabel';
import {
  resolveActiveRelativeIsoWeekTabId,
  resolveRelativeIsoWeekNavBounds,
} from '~/utils/isoWeekPicker';
import { resolveTodayYmd, resolveDefaultTimezone } from '~/utils/timezone';
import {
  SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL,
  formatWorkoutRelativeDayLabel,
  isWorkoutCalendarDayKnown,
  resolveWorkoutRelativeDayNumber,
} from '~/utils/workoutScheduleContext';
import {
  collectScheduleOverlapWorkoutIds,
  detectWorkoutScheduleOverlaps,
  resolveWorkoutScheduleOverlapKey,
} from '~/utils/workoutScheduleOverlap';

export const SCHEDULE_WEEK_PICK_VALUE = 'pick';

export const SCHEDULE_WEEK_UNDATED_LABEL = SCHEDULE_WEEK_UNDETERMINED_DAY_LABEL;

const SCHEDULE_WEEK_CATALOG_CONFIGS = new Set([
  'schedule-trainer-week',
  'schedule-client-week',
]);

const SCHEDULE_PERIOD_CATALOG_CONFIGS = new Set([
  ...SCHEDULE_WEEK_CATALOG_CONFIGS,
  'schedule-trainer-day',
  'schedule-client-day',
]);

export function isScheduleWeekCatalogConfig(config) {
  return SCHEDULE_PERIOD_CATALOG_CONFIGS.has(config);
}

export function isScheduleWeekCatalogData(catalogItems) {
  return Boolean(
    catalogItems
    && !Array.isArray(catalogItems)
    && Array.isArray(catalogItems.days)
    && catalogItems.days.length > 0,
  );
}

function resolveTrainingDateYmd(raw) {
  if (!raw) {
    return '';
  }
  if (typeof raw === 'object' && raw.val) {
    return String(raw.val).slice(0, 10);
  }
  return String(raw).slice(0, 10);
}

function resolveScalarValue(raw) {
  if (raw && typeof raw === 'object' && 'val' in raw) {
    return raw.val;
  }
  return raw;
}

export function resolveScheduleWeekStartYmd(catalogDays) {
  const firstDay = (catalogDays || []).find((day) => day?.date);
  return firstDay?.date ? String(firstDay.date).slice(0, 10) : '';
}

export function normalizeScheduleCatalogWorkout(raw) {
  const scheduleContext = raw?.schedule_context ?? null;
  const calendarDayKnown = isWorkoutCalendarDayKnown(scheduleContext);
  let dateSort = '';
  if (calendarDayKnown) {
    dateSort = resolveTrainingDateYmd(raw?.training_date);
    if (!dateSort) {
      const effectiveDate = String(scheduleContext?.effective_date ?? '').slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
        dateSort = effectiveDate;
      }
    }
  }
  const startTime = resolveScalarValue(raw?.start_time);
  const isMissingSlot = raw?.status === 'missing_slot' || Boolean(raw?.is_missing_slot);

  const isCompleted = Boolean(
    raw?.ent_is_completed
    ?? raw?.entity_workflow?.ent_is_completed,
  );

  let status = 'scheduled';
  if (isMissingSlot) {
    status = 'missing_slot';
  } else if (isCompleted) {
    status = 'completed';
  }

  return {
    id: raw?.uuid ?? raw?.id,
    uuid: raw?.uuid ?? raw?.id,
    title: raw?.title,
    url: raw?.url,
    date_sort: dateSort,
    start_time: startTime,
    status,
    is_missing_slot: isMissingSlot,
    delivery_format: raw?.delivery_format ?? raw?.training_format ?? null,
    training_format: raw?.training_format ?? raw?.delivery_format ?? null,
    delivery_format_label: raw?.delivery_format_label ?? null,
    person_name: raw?.person_name ?? null,
    person_avatar: raw?.person_avatar ?? null,
    person_uuid: raw?.person_uuid ?? raw?.trainee_uuid ?? null,
    ent_is_completed: raw?.ent_is_completed ?? raw?.entity_workflow?.ent_is_completed ?? false,
    ent_is_started: raw?.ent_is_started ?? raw?.entity_workflow?.ent_is_started ?? false,
    workout_exercises_confirmed: raw?.workout_exercises_confirmed,
    workout_prescription_weights_confirmed: raw?.workout_prescription_weights_confirmed,
    workout_sets_confirmed: raw?.workout_sets_confirmed,
    workout_analysis_confirmed: raw?.workout_analysis_confirmed,
    workout_result_checked: raw?.workout_result_checked,
    workout_has_remarks: raw?.workout_has_remarks,
    prescription_needs_weights: raw?.prescription_needs_weights ?? raw?.workout_prescription_needs_weights,
    prescription_weights_complete: raw?.prescription_weights_complete ?? raw?.workout_prescription_weights_complete,
    workout_status: raw?.workout_status ?? null,
    cancel_reason: raw?.cancel_reason ?? null,
    schedule_context: scheduleContext,
  };
}

function attachScheduleWeekWorkoutSchedule(workout, isUndatedDay = false) {
  const dayUnknown = isUndatedDay || !isWorkoutCalendarDayKnown(workout?.schedule_context);

  if (dayUnknown) {
    const time = formatWorkoutScheduleParts(workout).time;
    const hasTime = Boolean(String(workout?.start_time ?? '').trim());

    return {
      ...workout,
      date_sort: '',
      schedule: {
        date: formatWorkoutRelativeDayLabel(workout?.schedule_context),
        detail: '',
        weekNote: '',
        hint: '',
        time,
        hasDate: false,
        hasTime,
      },
    };
  }

  return {
    ...workout,
    schedule: formatWorkoutScheduleParts(workout),
  };
}

function compareScheduleWeekWorkouts(left, right) {
  const leftUndated = !String(left?.date_sort ?? '').trim();
  const rightUndated = !String(right?.date_sort ?? '').trim();
  if (leftUndated !== rightUndated) {
    return leftUndated ? 1 : -1;
  }

  if (leftUndated && rightUndated) {
    const leftRelativeDay = resolveWorkoutRelativeDayNumber(left?.schedule_context) ?? Number.MAX_SAFE_INTEGER;
    const rightRelativeDay = resolveWorkoutRelativeDayNumber(right?.schedule_context) ?? Number.MAX_SAFE_INTEGER;
    if (leftRelativeDay !== rightRelativeDay) {
      return leftRelativeDay - rightRelativeDay;
    }
  }

  const dateCompare = String(left.date_sort ?? '').localeCompare(String(right.date_sort ?? ''));
  if (dateCompare !== 0) {
    return dateCompare;
  }

  return String(left.start_time ?? '').localeCompare(String(right.start_time ?? ''));
}

export function buildScheduleWeekWorkouts(catalogDays) {
  const workouts = [];

  for (const day of catalogDays || []) {
    const isUndatedDay = Boolean(day?.is_undated);
    for (const rawWorkout of day?.workouts || []) {
      const normalized = normalizeScheduleCatalogWorkout(rawWorkout);
      if (isUndatedDay) {
        normalized.date_sort = '';
      }
      workouts.push(attachScheduleWeekWorkoutSchedule(normalized, isUndatedDay));
    }
  }

  return workouts.sort(compareScheduleWeekWorkouts);
}

export function resolveDefaultScheduleWeekFormatFilter() {
  return [];
}

export function resolveDefaultScheduleWeekStatusFilter() {
  return [];
}

export function isScheduleWeekFiltersActive(selectedFormats, selectedStatuses) {
  const formats = selectedFormats || [];
  const statuses = selectedStatuses || [];

  const formatsActive = formats.length > 0
    && formats.length < TRAINING_DELIVERY_FORMAT_FILTER_OPTIONS.length;
  const statusesActive = statuses.length > 0
    && statuses.length < ALL_WORKOUT_STATUS_FILTER_VALUES.length;

  return formatsActive || statusesActive;
}

export const SCHEDULE_TRAINER_FILTER_PRESETS = [
  {
    id: 'trainer_participation',
    label: 'Тренировки с моим участием',
    formats: ['gym_together', 'video_together'],
    statuses: [],
    icons: [
      { kind: 'format', value: 'gym_together' },
      { kind: 'format', value: 'video_together' },
    ],
  },
  {
    id: 'needs_filling',
    label: 'Тренировки, требующие наполнения',
    formats: [],
    statuses: ['draft'],
    icons: [
      { kind: 'status', value: 'draft' },
    ],
  },
];

function areScheduleFilterValuesEqual(left, right) {
  const leftSet = new Set(left || []);
  const rightSet = new Set(right || []);
  if (leftSet.size !== rightSet.size) {
    return false;
  }
  for (const value of leftSet) {
    if (!rightSet.has(value)) {
      return false;
    }
  }
  return true;
}

export function resolveActiveScheduleTrainerFilterPreset(formats, statuses) {
  for (const preset of SCHEDULE_TRAINER_FILTER_PRESETS) {
    if (
      areScheduleFilterValuesEqual(formats, preset.formats)
      && areScheduleFilterValuesEqual(statuses, preset.statuses)
    ) {
      return preset.id;
    }
  }
  return null;
}

export function filterScheduleWeekWorkoutsByFormats(workouts, selectedFormats) {
  const formats = selectedFormats || [];
  if (
    !formats.length
    || formats.length >= TRAINING_DELIVERY_FORMAT_FILTER_OPTIONS.length
  ) {
    return workouts || [];
  }

  return (workouts || []).filter((workout) => {
    const formatKey = resolveWorkoutDeliveryFormatKey(workout);
    if (!formatKey) {
      return formats.includes(TRAINING_DELIVERY_FORMAT_UNSET);
    }
    return formats.includes(formatKey);
  });
}

export function resolveScheduleWorkoutStatusFilterVariant(workout) {
  return resolveWorkoutOverviewStatusFilterVariant(
    workout,
    buildWorkoutOverviewStatusSourceFromPlanWorkout,
  );
}

export function filterScheduleWeekWorkoutsByStatuses(workouts, selectedStatuses) {
  const statuses = selectedStatuses || [];
  if (
    !statuses.length
    || statuses.length >= ALL_WORKOUT_STATUS_FILTER_VALUES.length
  ) {
    return workouts || [];
  }

  const normalizedStatuses = new Set(
    statuses.map((value) => normalizeWorkoutOverviewStatusFilterValue(value)),
  );

  return (workouts || []).filter((workout) => {
    const keys = resolveWorkoutStatusFilterKeys(
      workout,
      buildWorkoutOverviewStatusSourceFromPlanWorkout,
    );
    return keys.some((key) => normalizedStatuses.has(key));
  });
}

export function groupScheduleWeekWorkoutsByDate(workouts) {
  return groupWorkoutsByPlanDate(workouts);
}

export function formatScheduleWorkoutDateLabel(workout) {
  return formatWorkoutScheduleParts(workout).date;
}

export function isScheduleWeekPickMode(selectedWeek) {
  return selectedWeek === SCHEDULE_WEEK_PICK_VALUE;
}

export function resolveDefaultScheduleWeekPickDate(timezone = resolveDefaultTimezone()) {
  return resolveTodayYmd(timezone);
}

export function resolveScheduleWeekEndYmd(catalogDays) {
  const datedDays = (catalogDays || []).filter((day) => day?.date);
  if (datedDays.length > 0) {
    return String(datedDays[datedDays.length - 1].date).slice(0, 10);
  }

  const weekStart = resolveScheduleWeekStartYmd(catalogDays);
  if (!weekStart) {
    return '';
  }

  const iso = getIsoWeekInfo(weekStart);
  return iso?.dateEnd ?? '';
}

export function formatScheduleWeekPickRangeLabel(dateStart, dateEnd) {
  const period = formatCompactWeekDateRangeLabel(dateStart, dateEnd);
  if (!period) {
    return '';
  }

  const parts = period.split(' - ');
  if (parts.length === 2) {
    return `с ${parts[0]} по ${parts[1]}`;
  }

  return period;
}

export function resolveScheduleWeekPickSectionTitle(dateStart, dateEnd) {
  const range = formatScheduleWeekPickRangeLabel(dateStart, dateEnd);
  if (!range) {
    return {
      prefix: 'Тренировки',
      pickLabel: 'Выбрать неделю',
    };
  }

  return {
    prefix: `Тренировки ${range}`,
    pickLabel: 'Выбрать неделю',
  };
}

const SCHEDULE_WEEK_TITLE_ADJECTIVES = {
  '-1': 'прошлой',
  0: 'текущей',
  1: 'следующей',
};

export function resolveScheduleWeekSectionTitle(weekShift, options = {}) {
  if (isScheduleWeekPickMode(weekShift)) {
    return resolveScheduleWeekPickSectionTitle(
      options.dateStart,
      options.dateEnd,
    );
  }

  const adjective = SCHEDULE_WEEK_TITLE_ADJECTIVES[weekShift] ?? SCHEDULE_WEEK_TITLE_ADJECTIVES[0];
  return `Тренировки ${adjective} недели`;
}

const SCHEDULE_WEEK_SUB_NAV_LABELS = {
  '-1': 'Прошлая',
  0: 'Текущая',
  1: 'Следующая',
};

function resolveScheduleWeekMetaSubLabel(weekShift, options = {}) {
  if (isScheduleWeekPickMode(weekShift)) {
    const { prefix } = resolveScheduleWeekPickSectionTitle(
      options.dateStart,
      options.dateEnd,
    );
    const rangeLabel = String(prefix || '').replace(/^Тренировки\s+/i, '').trim();
    return rangeLabel || 'Выбрать';
  }

  return SCHEDULE_WEEK_SUB_NAV_LABELS[String(weekShift)] ?? SCHEDULE_WEEK_SUB_NAV_LABELS[0];
}

export function resolveScheduleWeekMetaTitle(weekShift, options = {}) {
  const subLabel = resolveScheduleWeekMetaSubLabel(weekShift, options);
  return ['Расписание тренировок', 'На неделю', subLabel].join(' — ');
}

export function resolveScheduleWeekMetaTitleFromBounds(bounds) {
  const relativeTabId = resolveActiveRelativeIsoWeekTabId(
    bounds,
    resolveRelativeIsoWeekNavBounds(),
  );
  const relativeLabels = {
    past: 'Прошлая',
    active: 'Текущая',
    next: 'Следующая',
  };
  const relativeLabel = relativeLabels[relativeTabId]
    || formatCompactWeekDateRangeLabel(bounds?.dateStart, bounds?.dateEnd)
    || 'Текущая';

  return ['Расписание тренировок', 'На неделю', relativeLabel].join(' — ');
}

export function resolveScheduleWeekContentEyebrow(bounds) {
  const relativeTabId = resolveActiveRelativeIsoWeekTabId(
    bounds,
    resolveRelativeIsoWeekNavBounds(),
  );

  if (relativeTabId === 'past') {
    return 'Тренировки прошлой недели';
  }
  if (relativeTabId === 'active') {
    return 'Тренировки текущей недели';
  }
  if (relativeTabId === 'next') {
    return 'Тренировки следующей недели';
  }

  const range = formatScheduleWeekPickRangeLabel(bounds?.dateStart, bounds?.dateEnd);
  return range ? `Тренировки ${range}` : 'Тренировки';
}

export function collectScheduleWeekClientUuids(workouts) {
  const uuids = new Set();

  for (const workout of workouts || []) {
    const uuid = String(workout?.person_uuid ?? '').trim();
    if (uuid) {
      uuids.add(uuid);
    }
  }

  return uuids;
}

/**
 * Тренировки с участием тренера, пересекающиеся по времени на неделе.
 *
 * @param {Array<{ date?: string, workouts?: unknown[] }>} catalogDays
 */
export function filterScheduleOverlapWorkouts(catalogDays) {
  const workouts = buildScheduleWeekWorkouts(catalogDays);
  const overlapIds = collectScheduleOverlapWorkoutIds(
    detectWorkoutScheduleOverlaps(workouts),
  );

  if (!overlapIds.size) {
    return [];
  }

  return workouts.filter((workout) =>
    overlapIds.has(resolveWorkoutScheduleOverlapKey(workout)),
  );
}
